import { useState, useEffect, useCallback } from 'react';

import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import { ContentItem } from 'domains/core/components/AutocompleteContent/AutocompleteContent';

export type ContentSelectionHookProps = {
    contentId: number | null;
    contentList: ContentItem[];
    onContentChange: (contentId: number | null, contentName: string) => void;
};

const resolveOption = (
    contentId: number | null,
    contentList: ContentItem[]
): { option: AutocompleteOption<ContentItem> | null; inputValue: string; unavailable: boolean } => {
    if (contentId && contentList?.length) {
        const idx = contentList.findIndex((item) => item.id === contentId);
        if (idx !== -1) {
            const item = contentList[idx];
            return {
                option: { index: idx + 1, label: item.name, value: item },
                inputValue: item.name,
                unavailable: false,
            };
        }
        // ID exists but not in list — content was unpublished
        return { option: null, inputValue: '', unavailable: true };
    }
    return { option: null, inputValue: '', unavailable: false };
};

export const useContentSelection = ({ contentId, contentList, onContentChange }: ContentSelectionHookProps) => {
    // Initialise synchronously so the pre-selected content shows on the first paint (avoids a
    // visible "Select or create content" flash when navigating back to a step that already has
    // content — React Query often has cached data available on the very first render).
    const [contentInputValue, setContentInputValue] = useState(() => resolveOption(contentId, contentList).inputValue);
    const [selectedOption, setSelectedOption] = useState<AutocompleteOption<ContentItem> | null>(
        () => resolveOption(contentId, contentList).option
    );
    const [isContentUnavailable, setIsContentUnavailable] = useState(
        () => resolveOption(contentId, contentList).unavailable
    );

    // Keep in sync when contentId or contentList changes after mount
    useEffect(() => {
        const { option, inputValue, unavailable } = resolveOption(contentId, contentList);
        setSelectedOption(option);
        setContentInputValue(inputValue);
        setIsContentUnavailable(unavailable);
    }, [contentId, contentList]);

    const handleContentSelection = useCallback(
        (_e: React.SyntheticEvent, option: AutocompleteOption<ContentItem> | null) => {
            setSelectedOption(option);

            if (!option) {
                // Handle clearing the selection
                onContentChange(null, '');
                return;
            }

            // Let CreateNewDropDownItem's onClick handle index 0
            if (option.index !== 0 && option.value) {
                onContentChange(option.value.id, option.value.name);
            }
        },
        [onContentChange]
    );

    const handleInputChange = useCallback((_e: React.SyntheticEvent, value: string) => {
        setContentInputValue(value);
    }, []);

    return {
        contentInputValue,
        selectedOption,
        isContentUnavailable,
        unavailableContentId: isContentUnavailable ? contentId : null,
        handleContentSelection,
        handleInputChange,
    };
};
